import XLSX from "xlsx-js-style";

const WEEKEND_COLOR = "FCE4D6";
const HOLIDAY_COLOR = "EF9A9A";
const WHITE = "FFFFFF";

const FIRST_DAY_COL = 2; // column C (0-based index)
const MAX_DAYS = 31; // C..AG
const TOTAL_COL = FIRST_DAY_COL + MAX_DAYS; // AH

const thin = { style: "thin", color: { rgb: "000000" } };
const BORDER = { top: thin, bottom: thin, left: thin, right: thin };

const cellRef = (r, c) => XLSX.utils.encode_cell({ r, c });

/** "2026-10" -> { year: 2026, month: 10, daysInMonth: 31 } */
function parseDate(date) {
  const [y, m] = String(date).split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) {
    throw new Error(`Invalid date "${date}", expected format "YYYY-MM"`);
  }
  return { year: y, month: m, daysInMonth: new Date(Date.UTC(y, m, 0)).getUTCDate() };
}

const pad = (n) => String(n).padStart(2, "0");

/** Background color for a day column (holiday wins over weekend), or WHITE. */
function dayColor(year, month, day, holidaySet) {
  if (holidaySet.has(`${year}-${pad(month)}-${pad(day)}`)) return HOLIDAY_COLOR;
  const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay(); // 0 = Sun, 6 = Sat
  if (dow === 0 || dow === 6) return WEEKEND_COLOR;
  return WHITE;
}

function style({ bg = WHITE, font = {}, align = {} } = {}) {
  return {
    fill: { patternType: "solid", fgColor: { rgb: bg } },
    font: { name: "Calibri", sz: 11, ...font },
    alignment: { vertical: "bottom", ...align },
    border: BORDER,
  };
}

/**
 * Builds the timesheet workbook.
 * @param {Object}   params
 * @param {string}   params.sheetName  e.g. "2026.10"
 * @param {string[]} params.holidays   e.g. ["2026-10-31"]
 * @param {string}   params.date       e.g. "2026-10"
 * @param {Object}   params.data       employees keyed by id (see structure in the request)
 * @returns {XLSX.WorkBook}
 */
export function buildTimesheetWorkbook({ sheetName, holidays = [], date, data }) {
  const { year, month, daysInMonth } = parseDate(date);
  const holidaySet = new Set(holidays);

  // Background for every day column (index 1..31). Columns beyond the month stay white.
  const colBg = {};
  for (let d = 1; d <= MAX_DAYS; d++) {
    colBg[d] = d <= daysInMonth ? dayColor(year, month, d, holidaySet) : WHITE;
  }

  const ws = {};
  const rowHeights = [];

  // ---------- Header row ----------
  ws[cellRef(0, 0)] = { t: "s", v: "Tab.Nr.", s: style() };
  ws[cellRef(0, 1)] = {
    t: "s",
    v: "Darbuotojas",
    s: style({
      font: { name: "Times New Roman", sz: 14, bold: true },
      align: { horizontal: "center", vertical: "center", wrapText: true },
    }),
  };
  for (let d = 1; d <= MAX_DAYS; d++) {
    const s = style({
      bg: colBg[d],
      font: { name: "Times New Roman", sz: 14, bold: true },
      align: { horizontal: "center", vertical: "center" },
    });
    const ref = cellRef(0, FIRST_DAY_COL + d - 1);
    ws[ref] = d <= daysInMonth ? { t: "n", v: d, s } : { t: "s", v: "", s };
  }
  ws[cellRef(0, TOTAL_COL)] = {
    t: "s",
    v: "Iš viso valandų",
    s: style({ font: { sz: 10, bold: true } }),
  };
  rowHeights.push({ hpt: 18.4 });

  // ---------- Employee rows ----------
  let row = 1;
  const employees = Object.values(data || {});

  for (const emp of employees) {
    const totalRow = row;
    const nightRow = row + 1;

    // map day -> entry
    const byDay = new Map();
    for (const e of emp.data || []) byDay.set(Number(e.day), e);

    // Column A: external_id (only on the first row)
    const ext = emp.external_id;
    const extIsNum = ext !== null && ext !== undefined && ext !== "" && !isNaN(Number(ext));
    ws[cellRef(totalRow, 0)] =
      ext === null || ext === undefined || ext === ""
        ? { t: "s", v: "", s: style() }
        : extIsNum
          ? { t: "n", v: Number(ext), s: style() }
          : { t: "s", v: String(ext), s: style() };
    ws[cellRef(nightRow, 0)] = { t: "s", v: "", s: style() };

    // Column B: name / "iš jų naktį"
    ws[cellRef(totalRow, 1)] = { t: "s", v: emp.name ?? "", s: style() };
    ws[cellRef(nightRow, 1)] = {
      t: "s",
      v: "iš jų naktį",
      s: style({ align: { horizontal: "right" } }),
    };

    // Day columns
    let sumTotal = 0;
    let sumNight = 0;
    for (let d = 1; d <= MAX_DAYS; d++) {
      const c = FIRST_DAY_COL + d - 1;
      const bg = colBg[d];
      const inMonth = d <= daysInMonth;
      const entry = inMonth ? byDay.get(d) : undefined;

      const totalStyle = style({ bg, align: { horizontal: "right" } });
      const nightStyle = style({ bg });

      if (!inMonth) {
        ws[cellRef(totalRow, c)] = { t: "s", v: "", s: totalStyle };
        ws[cellRef(nightRow, c)] = { t: "s", v: "", s: nightStyle };
        continue;
      }

      const hasTotal = entry && entry.total_hours !== null && entry.total_hours !== undefined;
      if (hasTotal) {
        ws[cellRef(totalRow, c)] = { t: "n", v: entry.total_hours, s: totalStyle };
        sumTotal += entry.total_hours;

        const hasNight = entry.night_hours !== null && entry.night_hours !== undefined;
        if (hasNight) {
          ws[cellRef(nightRow, c)] = { t: "n", v: entry.night_hours, s: nightStyle };
          sumNight += entry.night_hours;
        } else {
          ws[cellRef(nightRow, c)] = { t: "s", v: "", s: nightStyle };
        }
      } else {
        ws[cellRef(totalRow, c)] = { t: "s", v: "P", s: totalStyle };
        ws[cellRef(nightRow, c)] = { t: "s", v: "", s: nightStyle };
      }
    }

    // Column AH: totals (live formulas + cached values)
    const first = XLSX.utils.encode_col(FIRST_DAY_COL);
    const last = XLSX.utils.encode_col(FIRST_DAY_COL + MAX_DAYS - 1);
    const round1 = (n) => Math.round(n * 10) / 10;
    ws[cellRef(totalRow, TOTAL_COL)] = {
      t: "n",
      f: `SUM(${first}${totalRow + 1}:${last}${totalRow + 1})`,
      v: round1(sumTotal),
      s: style(),
    };
    ws[cellRef(nightRow, TOTAL_COL)] = {
      t: "n",
      f: `SUM(${first}${nightRow + 1}:${last}${nightRow + 1})`,
      v: round1(sumNight),
      s: style(),
    };

    rowHeights.push({ hpt: 13.55 }, { hpt: 13.55 });
    row += 2;
  }

  // ---------- Sheet settings ----------
  ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: Math.max(row - 1, 0), c: TOTAL_COL } });
  ws["!cols"] = [
    { wch: 9.5 }, // A
    { wch: 23.85 }, // B
    ...Array.from({ length: MAX_DAYS }, () => ({ wch: 5.5 })), // C..AG
    { wch: 14.5 }, // AH
  ];
  ws["!rows"] = rowHeights;
  ws["!pageSetup"] = { orientation: "landscape" };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, String(sheetName).slice(0, 31));
  return wb;
}

/**
 * Generates the timesheet and saves it.
 * - In the browser it triggers a download.
 * - In Node it writes the file to disk.
 * Returns the workbook.
 */
export function generateTimesheet({ sheetName, holidays, date, data }, fileName) {
  const wb = buildTimesheetWorkbook({ sheetName, holidays, date, data });
  XLSX.writeFile(wb, fileName || `timesheet_${sheetName}.xlsx`);
  return wb;
}

/** Same workbook, returned as a Buffer / Uint8Array (e.g. for sending over HTTP). */
export function generateTimesheetBuffer({ sheetName, holidays, date, data }) {
  const wb = buildTimesheetWorkbook({ sheetName, holidays, date, data });
  return XLSX.write(wb, { bookType: "xlsx", type: "buffer" });
}